import {
  RadioBtn,
  RadioGrp,
  SelectInputStyled,
  TextAreaInputStyled,
} from '@edulastic/common'
import { Form, Select } from 'antd'
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { segmentApi } from '@edulastic/api'
import AIMultiSelectItemTypeInfo from '@edulastic/common/src/components/AIMultiSelectItemTypeInfo'
import { getAlignmentDataForAiQuestions } from '../../AssessmentCreate/components/CreateAITest/ducks/helpers'
import { standardsFields } from '../../AssessmentPage/VideoQuiz/constants'
import StandardSet from '../../AssessmentPage/components/QuestionEditModal/common/StandardSet/StandardSet'
import { selectsData } from '../../TestPage/components/common'
import AIContentView from '../common/AIContentView'
import AIFooterAction from '../common/AIFooterAction'
import QuestionView from '../common/QuestionView'
import { StyledForm, StyledStandardSet } from '../common/styled'
import { DIFFICULTY_OPTIONS, DOK_OPTIONS } from '../constants/form'
import {
  generateAIPassageQuestionAction,
  getAIPassageQuestionState,
} from '../ducks/passageQuestionGeneration'
import { getPassagePreference } from '../utils/form'
import PromptSuggestions from '../common/PromptSuggestions'
import {
  PASSAGE_AUTHORING_PROMPTS,
  PROMPT_SUGGESTIONS,
} from '../constants/prompt'

const AIPassageQuestionGeneration = ({
  form,
  aiPassageQuestionState,
  generateAIPassageQuestion,
  data: { passages = [] } = {},
  onFinish,
}) => {
  const [alignmentFieldValue, setAlignmentFieldValue] = useState()
  const { getFieldDecorator, getFieldValue, setFieldsValue } = form
  const { apiStatus, result, requestedData } = aiPassageQuestionState

  const alignment = alignmentFieldValue
    ? getAlignmentDataForAiQuestions(alignmentFieldValue)
    : {}

  const invalidGrades = !alignmentFieldValue?.[0]?.grades?.length
  const invalidSubject = !alignmentFieldValue?.[0]?.subject?.length

  const currentPrompt = PASSAGE_AUTHORING_PROMPTS.QUESTION_GENERATION

  const onSubmit = (e) => {
    e.preventDefault()
    form.validateFieldsAndScroll((err, values) => {
      if (!err && !invalidGrades && !invalidSubject) {
        let requestBody = {
          ...values,
        }

        if (requestBody.difficultLevels) {
          requestBody.difficultLevels = [requestBody.difficultLevels]
        } else if (requestBody.difficultLevels === '') {
          requestBody.difficultLevels = DIFFICULTY_OPTIONS.map(
            ({ value }) => value
          ).filter((value) => !!value)
        }

        requestBody.depthsOfKnowledge = DOK_OPTIONS.map(
          ({ value }) => value
        ).filter((value) => !!value)

        const {
          grades,
          subject,
          standardIds: commonCoreStandards,
          standardSet,
          standardDescriptions: commonCoresStandardDescriptions,
        } = alignment

        requestBody = {
          ...requestBody,
          grades: grades.length
            ? grades
            : selectsData.allGrades.map(({ value }) => value),
          subject,
          standardSet,
          commonCoreStandards,
          commonCoresStandardDescriptions,
          preference: getPassagePreference(passages, requestBody.preference),
        }

        if (requestBody.preference === '') {
          delete requestBody.preference
        }

        if (currentPrompt.trackingEvent) {
          segmentApi.genericEventTrack('GenerateAIItem', {
            source: currentPrompt.trackingEvent.source,
          })
        }

        generateAIPassageQuestion(requestBody)
      }
    })
  }

  const onAlignmentChange = (data) => {
    setAlignmentFieldValue(data.alignment)
  }

  let errorMessage = ''
  if (invalidGrades && invalidSubject) {
    errorMessage = 'Please select grade and subject!'
  } else if (invalidGrades) {
    errorMessage = 'Please select grade!'
  } else if (invalidSubject) {
    errorMessage = 'Please select subject!'
  }

  const isLoading = apiStatus === 'INITIATED'

  return (
    <StyledForm onSubmit={onSubmit}>
      <Form.Item
        validateStatus={errorMessage ? 'error' : 'success'}
        help={errorMessage}
        required
        label="STANDARD"
      >
        <StyledStandardSet>
          <StandardSet
            disabled={isLoading}
            alignment={alignmentFieldValue}
            onUpdate={onAlignmentChange}
            showIconBrowserBtn
            hideLabel
            standardsRequiredFields={[
              standardsFields.SUBJECT,
              standardsFields.GRADES,
              standardsFields.STANDARD_SET,
            ]}
            isDocBased
            required={false}
            trackingEventSource="AI Passage Item Authoring"
            isAIFlow
          />
        </StyledStandardSet>
      </Form.Item>
      <Form.Item label="NUMBER OF QUESTIONS">
        {getFieldDecorator('count', {
          rules: [
            {
              required: true,
              message: 'Please select number of questions',
            },
          ],
        })(
          <SelectInputStyled
            showArrow
            showSearch
            height="36px"
            disabled={isLoading}
            placeholder="Select"
            dropdownStyle={{ zIndex: 1005 }}
          >
            {[...Array(10).keys()].map((countIndex) => (
              <Select.Option key={countIndex + 1} value={countIndex + 1}>
                {countIndex + 1}
              </Select.Option>
            ))}
          </SelectInputStyled>
        )}
      </Form.Item>
      <Form.Item label="QUESTION TYPES">
        {getFieldDecorator('questionTypes', {
          rules: [
            {
              required: true,
              message: 'Please select question types',
            },
          ],
        })(
          <SelectInputStyled
            showArrow
            showSearch
            data-cy="aiItemTypes"
            optionFilterProp="children"
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            height="36px"
            disabled={isLoading}
            placeholder="Select Question Types"
            mode="multiple"
            dropdownStyle={{ zIndex: 1005 }}
            aria-label="Select question types for AI generation"
            aria-describedby="passage-ai-item-generation-item-types-description"
            role="listbox"
            aria-expanded={false}
            aria-multiselectable="true"
            tabIndex={0}
          >
            {selectsData.allQuestionTypes.map(({ value, text }) => (
              <Select.Option
                data-cy={text}
                key={value}
                value={value}
                aria-label={`Question type: ${text}`}
              >
                {text}
              </Select.Option>
            ))}
          </SelectInputStyled>
        )}
        {getFieldValue('questionTypes')?.length > 1 && (
          <AIMultiSelectItemTypeInfo />
        )}
        <div
          id="passage-ai-item-generation-item-types-description"
          className="sr-only"
        >
          Select one or more item types for AI question generation. AI will
          create questions based on your selection.
        </div>
      </Form.Item>
      <Form.Item label="DIFFICULTY (OPTIONAL)">
        {getFieldDecorator('difficultLevels', {
          initialValue: '',
        })(
          <RadioGrp disabled={isLoading}>
            {DIFFICULTY_OPTIONS.map(({ value, label }, index) => (
              <RadioBtn key={index} value={value} data-cy={label}>
                {label}
              </RadioBtn>
            ))}
          </RadioGrp>
        )}
      </Form.Item>
      <Form.Item label="PREFERENCE (OPTIONAL)">
        {getFieldDecorator('preference', {
          rules: [
            {
              max: 500,
              message: 'This field should not exceed 500 characters!',
            },
          ],
        })(
          <TextAreaInputStyled
            disabled={isLoading}
            autoSize={{ minRows: 4 }}
            placeholder="Enter details here"
            data-cy="aiPassagePreferenceInput"
          />
        )}
      </Form.Item>
      <PromptSuggestions
        disabled={isLoading}
        currentPrompt={getFieldValue('preference')}
        suggestions={PROMPT_SUGGESTIONS.PASSAGE_QUESTION_PREFERENCE}
        onSelect={(preference) => {
          setFieldsValue({
            preference,
          })
        }}
      />
      <br />
      {apiStatus && (
        <div style={{ marginBottom: '90px' }}>
          <AIContentView
            onRegenerate={onSubmit}
            title="Created by AI Assistant for Passage Item Generation"
            loadingTitle="Generating item"
            content={
              <>
                {result?.map((questionData, index) => {
                  return (
                    <QuestionView
                      questionIndex={index + 1}
                      question={questionData}
                      questionType={questionData.type}
                    />
                  )
                })}
              </>
            }
            status={apiStatus}
          />
        </div>
      )}
      <AIFooterAction
        status={apiStatus}
        onRegenerate={onSubmit}
        action={{
          label: 'ADD QUESTIONS',
          onClick: () => {
            onFinish({
              questions: result,
              questionTypes: requestedData?.questionTypes,
              alignment: alignmentFieldValue?.[0],
            })
          },
        }}
        isLoading={isLoading}
      />
    </StyledForm>
  )
}

const enhance = compose(
  Form.create({ name: 'ai-question-generation' }),
  connect(
    (state) => ({
      aiPassageQuestionState: getAIPassageQuestionState(state),
    }),
    {
      generateAIPassageQuestion: generateAIPassageQuestionAction,
    }
  )
)

export default enhance(AIPassageQuestionGeneration)
